/**
 * L1 — per-pillar scoring from TYPED CANONICAL FACTS + report stamps, with
 * prose-regex demoted to an explicitly-labeled FALLBACK tier.
 *
 * ─── L-tier pillar-scoring rebuild ─────────────────────────────────────
 *
 * Previously every pillar was scored by REGEX OVER REPORT PROSE (headroom /
 * queue / voltage lifted from narrative text), silent `risk = 3` fillers, and
 * a "heuristic from report overall score" fallback — weakly coupled to
 * evidence, strongly coupled to LLM word choice. This rebuild scores each
 * pillar from the typed canonical-fact layer FIRST:
 *
 *   1. TYPED FACTS — `buildScoringFactSlice({ sed: evidence, report })`
 *      resolves the scoring-critical facts (grid / flood / zoning / wetlands /
 *      LCOE / CAPEX / developer land claims) through the SAME descriptor
 *      table + tier reconciliation the report-grounding layer uses
 *      (src/lib/grounding). Each per-fact contribution carries
 *      `{factKey, value, factProvenance, weight, score}` so the deep dive can
 *      trace every pillar score to its inputs.
 *   2. PROSE FALLBACK — the legacy regexes fire ONLY when no typed fact
 *      resolves, are labeled `factProvenance: 'prose-heuristic'`, and carry
 *      reduced confidence.
 *   3. MISSING — a fact that resolves at NO tier is EXCLUDED from the pillar
 *      mean and emitted as a `missingFacts` gap entry. A pillar with no
 *      resolved signal at all is marked `noScoringSignal` and composes to a
 *      MISSING pillar ("Awaiting Data") — never a silent amber-3 or a
 *      default-red floor presented as a measurement. (This deliberately
 *      RETIRES the legacy `risk = 3` filler and the "heuristic from report
 *      overall score" fallback.)
 *
 * Scoring scale: internal risk 1 (good) → 5 (bad); public benefit 0–5 via
 * `riskToBenefit`. Per-fact grading BANDS are unchanged from the legacy
 * heuristics (headroom load-relative vs `RESERVE_MARGIN_PCT`, queue-year
 * bands, voltage/distance bands, capex/LCOE bands) — only the SOURCE of the
 * graded values moved from prose to typed facts, and multi-signal pillars
 * combine by WEIGHTED MEAN instead of the old min/max walk from a silent 3.
 * Structured floors (the ELCC firm-capacity verdict) still apply AFTER the
 * mean and only ever worsen.
 *
 * Every exported signature is UNCHANGED (compositeScorer consumes them);
 * all new contribution fields are additive + optional.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { RiskItem, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type {
  FactProvenance,
  PillarCompositeScore,
  PillarConstraint,
  PillarFactContribution,
  PillarMissingFact,
} from '../../types/portfolioComposite';
import { getReportFieldValue } from '../sectionFieldPaths';
import { resolveReportCorrectedLcoe, worstCorrectedLcoe } from '../finDecision/reportLcoeSurface';
import { buildScoringFactSlice } from '../grounding/buildCanonicalFactStore';
import type { CanonicalFact, FactStatus } from '../grounding/canonicalFacts';
import {
  PILLARS,
  RISK_CATEGORY_TO_PILLAR,
  soleDecisionPillarForReportType,
  type PillarId,
} from './pillarRegistry';
import { isUnmitigatedRisk, scoringRisksForReport } from './scoringRisks';
import { PORTFOLIO_CONFIG } from './portfolioConfig';
import { waterScreenHasDeniedPermit, waterScreenText } from './waterScreenEvidence';
import { siteLoadQueueYearsFromText } from '../gridQueueEvidence';
import { qualifiedLocalSentimentEntries } from '../sentiment/qualifiedLocalSentiment';
import { assessPublicSentiment } from '../sentiment/publicSentimentScope';
import { resolveAllocatedServiceEvidence, type PowerScoringAuthorityContext } from './allocatedServiceEvidence';

export interface PillarScoreContribution {
  /** Benefit score 0–5 (higher = better site). */
  score: number;
  confidence: number;
  rationale: string;
  citations: (string | import('../../types/portfolioComposite').Citation)[];
  reportId: string;
  reportTypeId: ReportTypeId;
  /**
   * Part C SP2 — structured BINDING constraints (caps/floors) that moved this
   * contribution's score. Threaded up onto the composed pillar so the deep dive
   * can render them as chips. Empty/absent when nothing constrained the score.
   */
  constraints?: PillarConstraint[];
  /**
   * L-tier rebuild — the per-fact scoring inputs behind this contribution
   * (typed facts first; `'prose-heuristic'` entries are the labeled fallback).
   * Additive + optional.
   */
  factContributions?: PillarFactContribution[];
  /**
   * L-tier rebuild — scoring-critical facts this pillar expected but could
   * not resolve at any tier (excluded from the mean, listed as gaps).
   * Additive + optional.
   */
  missingFacts?: PillarMissingFact[];
  /**
   * L-tier rebuild — TRUE when NO fact and NO prose signal resolved for this
   * pillar. `composePillarScore` EXCLUDES such contributions from the score
   * mean (a pillar whose every contribution is signal-less composes to
   * MISSING / "Awaiting Data"). The carried `score` is a neutral placeholder
   * that must never be read as a measurement. Additive + optional; absent ⇒
   * a legacy/measured contribution.
   */
  noScoringSignal?: boolean;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/** Convert internal risk score (1=good, 5=bad) to benefit score for aggregation. */
export function riskToBenefit(riskScore: number): number {
  return clamp(6 - riskScore, PORTFOLIO_CONFIG.SCORE_MIN, PORTFOLIO_CONFIG.SCORE_MAX);
}

function firstNumber(text: string | undefined, patterns: RegExp[]): number | null {
  if (!text) return null;
  for (const re of patterns) {
    const m = text.match(re);
    if (m?.[1]) {
      const n = parseFloat(m[1].replace(/,/g, ''));
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

function countQaBlocks(report: SiteRagReport): number {
  const qa = report.qaFindings;
  if (!qa) return 0;
  let blocks = 0;
  for (const v of qa.providerPrecedenceViolations ?? []) {
    if (v.severity === 'block') blocks += 1;
  }
  for (const v of qa.internalNumberFindings ?? []) {
    if (v.severity === 'block') blocks += 1;
  }
  for (const v of qa.citationIntegrityViolations ?? []) {
    if (v.severity === 'block') blocks += 1;
  }
  for (const v of qa.distribution ?? []) {
    if (v.severity === 'block') blocks += 1;
  }
  for (const v of qa.docProvenanceFindings ?? []) {
    if (v.severity === 'block') blocks += 1;
  }
  for (const v of qa.sectionDriftFindings ?? []) {
    if (v.severity === 'block') blocks += 1;
  }
  return blocks;
}

/** Confidence from qaFindings: 1 - normalized block penalty (cap at 10 blocks). */
export function confidenceFromReport(report: SiteRagReport): number {
  const blocks = countQaBlocks(report);
  const penalty = Math.min(1, blocks / 10);
  return clamp(1 - penalty, 0, 1);
}

// R4a — `isUnmitigatedRisk` moved to `scoringRisks.ts` so this penalty and the
// rollup's PATH 6 cap read ONE rule (they used to carry two copies).

/** R4a — per-severity penalty on the 0–5 benefit scale. */
function penaltyForSeverity(severity: RiskItem['severity']): number {
  switch (severity) {
    case 'critical': return PORTFOLIO_CONFIG.FINDING_SEVERITY_PENALTY_CRITICAL;
    case 'high': return PORTFOLIO_CONFIG.FINDING_SEVERITY_PENALTY_HIGH;
    case 'medium': return PORTFOLIO_CONFIG.FINDING_SEVERITY_PENALTY_MEDIUM;
    case 'low': return PORTFOLIO_CONFIG.FINDING_SEVERITY_PENALTY_LOW;
    // Unknown severity from a legacy or malformed row scores NOTHING rather
    // than a guessed magnitude — never fabricate exposure.
    default: return 0;
  }
}

export interface FindingSeverityPenalty {
  /** Total penalty on the 0–5 benefit scale, already capped. */
  penalty: number;
  /** How many unmitigated risks contributed. */
  count: number;
  /** Highest severity among the contributing risks, for the constraint label. */
  worstSeverity: RiskItem['severity'] | null;
}

/**
 * R4a — the bounded finding-severity penalty a report imposes on ONE pillar.
 *
 * Reads only the STRUCTURED `report.risks[]` severity field — never narrative
 * prose — so this stays inside the "deterministic scoring is authoritative;
 * narrative may not raise, lower, or replace the computed verdict" doctrine
 * (`docs/architecture/decision-analysis.md`). Narrative supplies the TEXT of a
 * risk; the schema-constrained severity enum supplies its WEIGHT.
 *
 * Only risks whose `category` maps to `pillarId` count, so a finding lands on
 * one axis rather than moving all ten. PURE: no risks, unmitigated-none, or an
 * unmapped category ⇒ `{ penalty: 0 }` ⇒ byte-identical to the pre-R4 score.
 */
export function findingSeverityPenaltyForPillar(
  report: SiteRagReport,
  pillarId: PillarId,
): FindingSeverityPenalty {
  // Explicit `risks[]` plus the environmental report's projected matrix rows
  // (P1.7) — see `scoringRisks.ts` for the admissibility rule.
  const risks = scoringRisksForReport(report);
  if (risks.length === 0) {
    return { penalty: 0, count: 0, worstSeverity: null };
  }
  const order: RiskItem['severity'][] = ['low', 'medium', 'high', 'critical'];
  let raw = 0;
  let count = 0;
  let worstSeverity: RiskItem['severity'] | null = null;
  // A risk on a SINGLE-pillar report is about that pillar by construction, so
  // it counts regardless of the four-value category enum it was emitted under.
  // Without this, `water`, `land`, `financial` and `social` could never receive
  // a finding-severity penalty: a critical "no water rights, cannot be
  // permitted" is emitted as `environmental` and charged to the 0.08-weight
  // environmental pillar rather than the water pillar it describes.
  const solePillar = soleDecisionPillarForReportType(
    (report.reportType ?? 'feasibility') as Parameters<typeof soleDecisionPillarForReportType>[0],
  );
  for (const risk of risks) {
    const categoryPillar = RISK_CATEGORY_TO_PILLAR[risk.category];
    if (categoryPillar !== pillarId && solePillar !== pillarId) continue;
    if (!isUnmitigatedRisk(risk)) continue;
    const p = penaltyForSeverity(risk.severity);
    if (p <= 0) continue;
    raw += p;
    count += 1;
    if (worstSeverity === null || order.indexOf(risk.severity) > order.indexOf(worstSeverity)) {
      worstSeverity = risk.severity;
    }
  }
  return {
    penalty: Math.min(raw, PORTFOLIO_CONFIG.MAX_FINDING_SEVERITY_PENALTY),
    count,
    worstSeverity,
  };
}

function reportText(report: SiteRagReport): string {
  const parts: string[] = [];
  if (report.executiveSummary) parts.push(report.executiveSummary);
  if (report.summary) parts.push(report.summary);
  const rec = report as unknown as Record<string, unknown>;
  const md = rec.markdown;
  if (typeof md === 'string') parts.push(md);
  for (const f of report.structuredFindings ?? []) {
    parts.push(f.title, f.detail, f.impact);
  }
  return parts.join('\n');
}

function citationsFromFindings(report: SiteRagReport, limit = 5): (string | import('../../types/portfolioComposite').Citation)[] {
  const out: (string | import('../../types/portfolioComposite').Citation)[] = [];
  for (const f of report.structuredFindings ?? []) {
    if (f.source) {
      if (f.pageReference !== undefined) {
        out.push({ text: f.source, page: f.pageReference });
      } else {
        out.push(f.source);
      }
    }
    if (out.length >= limit) break;
  }
  if (out.length === 0 && report.references?.length) {
    for (const r of report.references.slice(0, limit)) {
      if (typeof r === 'string') out.push(r);
      else if (r && typeof r === 'object' && 'title' in r) out.push(String((r as { title?: string }).title ?? ''));
    }
  }
  return out.filter(Boolean);
}

/**
 * Planning reserve margin (%). Available grid headroom must clear the site's own
 * facility load PLUS this margin to be judged "adequate". ~15% is the canonical
 * NERC planning reserve margin for a firm 24/7 load. Used only by the power
 * pillar's load-relative grading — it does not affect generation nameplate
 * sizing (that lives in `phaseFeasibility.resolveGenerationNameplateMW`).
 */
export const RESERVE_MARGIN_PCT = 15;

/**
 * TRACK-A — risk FLOOR applied by the firm-capacity (ELCC) verdict on the power
 * pillar. The structured `report.firmCapacityVerdict.worstVerdict` (persisted at
 * generation by `buildFirmCapacityVerdictSnapshot`) mirrors the Stage-2a headline
 * verdict cap (`capTierByFirmCapacity` in `reportVerdict.ts`): a declared
 * AND-combined power mix that cannot firm the 24/7 load must score DOWN.
 *
 * The risk scale is 1 (good) → 5 (bad). These are FLOORS (applied with
 * `Math.max`) so the verdict can only WORSEN the pillar, never improve it —
 * exactly as the verdict cap only ever lowers a tier:
 *
 *   'infeasible' → risk floor 5   (mirrors the 'red'   tier cap → benefit 1)
 *   'marginal'   → risk floor 3.5 (mirrors the 'amber' tier cap → benefit ~2.5)
 *   'feasible' / null → NO floor  (legacy behaviour preserved)
 */
const FIRM_CAPACITY_RISK_FLOOR: Record<'infeasible' | 'marginal', number> = {
  infeasible: 5,
  marginal: 3.5,
};

/**
 * ON-SITE GENERATION risk floors — the promoted Generation & Impact scenario's
 * own screens, scored on the pillars that already own those questions.
 *
 * Generation is deliberately NOT an eleventh pillar. Weights sum to 1.00, so a
 * new pillar rescales all ten and moves every site's composite — including
 * sites with no generation scenario at all — and, because a pillar with no
 * source report counts toward `unmeasuredPillarWeight`, it would also charge a
 * data-confidence penalty portfolio-wide for a signal that applies to BTM sites
 * only. The questions the promoted scenario answers already belong to `power`
 * (can this serve the load?) and `land` (does it fit the parcel?).
 *
 * These mirror `FIRM_CAPACITY_RISK_FLOOR` exactly, in mechanism and in
 * calibration: floors on the 1 (good) → 5 (bad) risk scale, applied with
 * `Math.max`, so they can only ever WORSEN a pillar. The platform has already
 * decided how severely "the declared power mix cannot firm the load" should
 * score; these are the on-site analogue of the same finding.
 *
 *   'fail'        → 5    (mirrors ELCC 'infeasible' — the fleet cannot serve
 *                        critical load under its own N-k criterion)
 *   'conditional' → 3.5  (mirrors ELCC 'marginal' — clears critical load but
 *                        misses the reserve-padded requirement)
 *
 * `indeterminate` deliberately gets NO floor. It means the screen could not be
 * computed on the frozen inputs — an evidence gap, not a capacity finding.
 * Asserting risk from it would be exactly the unearned assertion the truth-only
 * gate exists to prevent; the absence is reported as a missing fact instead, so
 * data confidence handles it.
 *
 * ELCC and this screen measure DIFFERENT things (declared nameplate × ELCC vs
 * the promoted fleet's N-k net plus reserve), so both may fire. Composing them
 * through `Math.max` means the worse one binds — never an additive penalty.
 */
const GENERATION_SCREEN_RISK_FLOOR: Record<'fail' | 'conditional', number> = {
  fail: 5,
  conditional: 3.5,
};

/**
 * LAND — the promoted fit's parcel containment.
 *
 * A containment failure currently reaches no score at all: the land pillar
 * grades acreage and zoning, so a 900-acre site whose promoted fit does not
 * physically contain still scores well on land. Floored at 4 rather than 5
 * because constructability is MITIGABLE in the sense the rollup already uses
 * for per-source floors — a fit failure is answerable with layout work, added
 * land or fewer blocks, unlike "cannot firm the load".
 *
 * Honest about its own reach: at `land` weight 0.05 this shifts a composite by
 * at most ~0.1 of a 1.0-wide band. It is here for VISIBILITY in the deep dive,
 * not to move verdicts.
 */
const GENERATION_CONTAINMENT_RISK_FLOOR = 4;

/**
 * Null-safe read of the promoted scenario's joint capacity screen, persisted on
 * the report at generation. Returns a floor-worthy verdict only; `pass`,
 * `indeterminate`, legacy projections and reports with no generation scenario
 * all leave the pillar unchanged.
 */
function generationScreenFloorVerdict(
  report: SiteRagReport,
): 'fail' | 'conditional' | null {
  const v = report.generationImpactProjection?.capacityAndResilience?.screenVerdict;
  return v === 'fail' || v === 'conditional' ? v : null;
}

/**
 * Null-safe read of the promoted fit's containment verdicts. Either an acreage
 * shortfall or published geometry outside the boundary is a containment
 * failure; `indeterminate` and absent leave the pillar unchanged.
 */
function generationContainmentFailed(report: SiteRagReport): 'acreage' | 'geometry' | null {
  const footprint = report.generationImpactProjection?.testFitFootprint;
  if (!footprint) return null;
  if (footprint.acreageFitVerdict === 'fail') return 'acreage';
  if (footprint.spatialFitVerdict === 'fail') return 'geometry';
  return null;
}

/**
 * TRACK-A — null-safe read of the structured firm-capacity verdict persisted on
 * the report at generation. Returns `'infeasible' | 'marginal'` only when the
 * snapshot exists AND its worst-case verdict is one that should score the power
 * pillar DOWN; otherwise `null` (legacy reports, OR-mode/single-source sites with
 * a null worst-verdict, or a 'feasible' verdict all leave the pillar unchanged).
 */
function firmCapacityFloorVerdict(
  report: SiteRagReport,
): 'infeasible' | 'marginal' | null {
  const v = report.firmCapacityVerdict?.worstVerdict;
  if (v === 'infeasible' || v === 'marginal') return v;
  return null;
}

/* ── Fact-signal plumbing ─────────────────────────────────────────────── */

/** One resolved scoring input on the internal RISK scale (1 good → 5 bad). */
interface FactSignal {
  factKey: string;
  label: string;
  /** Display-formatted value the grading used. */
  value: string;
  unit?: string;
  provenance: FactProvenance;
  sourceLabel?: string;
  /** Weight within the pillar's weighted risk mean. */
  weight: number;
  /** Risk 1–5 this signal graded to. */
  risk: number;
  /** Rationale phrase (kept in the legacy phrasing so consumers/tests hold). */
  phrase: string;
  /** Per-signal basis confidence (verified 1.0 … prose 0.5). */
  confidence: number;
}

/** The full evaluation of one pillar for one report. */
interface PillarEvaluation {
  signals: FactSignal[];
  missing: PillarMissingFact[];
  constraints: PillarConstraint[];
  /** Extra rationale phrases not tied to a signal (e.g. non-binding floors). */
  notes: string[];
  /** Power only — the structured ELCC risk floor (applied after the mean). */
  elccFloor?: { risk: number; verdict: 'infeasible' | 'marginal' };
  /**
   * A floor from the promoted Generation & Impact scenario, applied after the
   * mean exactly like `elccFloor`. Carries its own label so the deep-dive chip
   * names WHICH screen moved the score, not just that something did.
   */
  generationFloor?: { risk: number; label: string; kind: 'generation-screen-floor' | 'generation-containment-floor' };
  /**
   * D14 — FINANCIAL only. The deterministic verdict band (`financialAnalysis.
   * band`) floors the pillar risk, applied after the mean exactly like
   * `elccFloor`. Only ever WORSENS. See `FINANCIAL_RED_BAND_FLOOR` for why the
   * band needs a floor and not just a weighted signal.
   *
   * Records a `financial-band-floor` `PillarConstraint` when it binds. That kind
   * exists specifically for this case rather than reusing a capacity floor: an
   * unfinanceable MODEL is not a physical limit, and the fix is different
   * (reprice or restructure, not resite).
   */
  financialFloor?: { risk: number; label: string };
}

/** Basis confidence per canonical fact status. Prose fallback uses 0.5. */
const STATUS_CONFIDENCE: Record<Exclude<FactStatus, 'absent'>, number> = {
  verified: 1.0,
  estimated: 0.8,
  'labeled-unverifiable': 0.6,
};
const PROSE_CONFIDENCE = 0.5;

/** Map a canonical fact's winning source ref → the chip provenance class. */
function provenanceOfFact(fact: CanonicalFact): FactProvenance {
  const ref = fact.sources[0]?.ref ?? '';
  if (ref.startsWith('claim:')) return 'site-context-claim';
  if (ref.startsWith('report:')) return 'report-stamp';
  if (ref.startsWith('derived:')) return 'derived';
  return 'sed-slot';
}

/** Short rationale tag per provenance ("flood zone X [sed]"). */
const PROVENANCE_TAG: Record<FactProvenance, string> = {
  'sed-slot': '[sed]',
  'site-context-claim': '[claim]',
  'report-stamp': '[report]',
  derived: '[derived]',
  'prose-heuristic': '[prose]',
};

/** A resolved, usable canonical fact from the slice (absent ⇒ null). */
function presentFact(
  facts: ReadonlyMap<string, CanonicalFact>,
  factId: string,
): CanonicalFact | null {
  const fact = facts.get(factId);
  if (!fact || fact.status === 'absent' || fact.value === null || fact.value === undefined) {
    return null;
  }
  return fact;
}

/** The fact's numeric value, or null when it is not a finite number. */
function factNumber(fact: CanonicalFact | null): number | null {
  if (!fact) return null;
  return typeof fact.value === 'number' && Number.isFinite(fact.value) ? fact.value : null;
}

/** The fact's string value, or null. */
function factString(fact: CanonicalFact | null): string | null {
  if (!fact) return null;
  return typeof fact.value === 'string' && fact.value.trim() !== '' ? fact.value.trim() : null;
}

/** Build a FactSignal from a resolved canonical fact + its grading. */
function signalFromFact(args: {
  fact: CanonicalFact;
  weight: number;
  risk: number;
  phrase: string;
  valueOverride?: string;
}): FactSignal {
  const provenance = provenanceOfFact(args.fact);
  const status = args.fact.status as Exclude<FactStatus, 'absent'>;
  return {
    factKey: args.fact.factId,
    label: args.fact.label,
    value: args.valueOverride ?? String(args.fact.value),
    unit: args.fact.unit,
    provenance,
    sourceLabel: args.fact.sources[0]?.label,
    weight: args.weight,
    risk: clamp(args.risk, 1, 5),
    phrase: `${args.phrase} ${PROVENANCE_TAG[provenance]}`,
    confidence: STATUS_CONFIDENCE[status] ?? 0.8,
  };
}

/** Build a FactSignal for a prose-heuristic (regex fallback) extraction. */
function signalFromProse(args: {
  factKey: string;
  label: string;
  value: string;
  unit?: string;
  weight: number;
  risk: number;
  phrase: string;
}): FactSignal {
  return {
    factKey: args.factKey,
    label: args.label,
    value: args.value,
    unit: args.unit,
    provenance: 'prose-heuristic',
    weight: args.weight,
    risk: clamp(args.risk, 1, 5),
    phrase: `${args.phrase} ${PROVENANCE_TAG['prose-heuristic']}`,
    confidence: PROSE_CONFIDENCE,
  };
}

/** Duck-typed dot-path read into the (unknown-shaped) evidence document. */
function readEvidencePath(root: unknown, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = root;
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

/**
 * The rubric's documented 5.0 "blocking" grade — the worst risk a single fact
 * can assert. Reserved for facts with no legitimate counterexample: the project
 * cannot lawfully or physically proceed on this axis, not merely that it is
 * hard. A blocking fact still cannot force NO-GO on its own (it lowers the
 * pillar, which lowers the weighted mean); the standing policy is that a severe
 * finding caps the call rather than declining the site.
 */
const BLOCKING_RISK = 5;

/* ── Per-fact grading (bands preserved from the legacy heuristics) ────── */

/** Headroom grading — load-relative when the site load is known. */
function gradeHeadroom(
  headroom: number,
  load: number | null,
): { risk: number; phrase: string } {
  if (load != null) {
    const required = load * (1 + RESERVE_MARGIN_PCT / 100);
    if (headroom >= required) {
      return {
        risk: 1.5,
        phrase: `headroom ${headroom} MW ≥ ${required.toFixed(0)} MW (load ${load.toFixed(0)} MW + ${RESERVE_MARGIN_PCT}% reserve) (strong)`,
      };
    }
    if (headroom >= load) {
      return { risk: 2.5, phrase: `headroom ${headroom} MW covers load ${load.toFixed(0)} MW, thin reserve (moderate)` };
    }
    return { risk: 4, phrase: `headroom ${headroom} MW < load ${load.toFixed(0)} MW (short)` };
  }
  // Legacy absolute thresholds — used only when the site load is unknown.
  if (headroom >= 200) return { risk: 1.5, phrase: `headroom ${headroom} MW (strong)` };
  if (headroom >= 50) return { risk: 2.5, phrase: `headroom ${headroom} MW (moderate)` };
  return { risk: 4, phrase: `headroom ${headroom} MW (tight)` };
}

function gradeQueueYears(queueYears: number): { risk: number; phrase: string } {
  if (queueYears < 2) return { risk: 1.5, phrase: `queue ~${queueYears}y` };
  if (queueYears < 4) return { risk: 2.5, phrase: `queue ~${queueYears}y` };
  if (queueYears < 7) return { risk: 3.5, phrase: `queue ~${queueYears}y` };
  return { risk: 4.5, phrase: `queue ~${queueYears}y (long)` };
}

function gradeVoltageKv(voltage: number): { risk: number; phrase: string } {
  if (voltage >= 230) return { risk: 2, phrase: `${voltage} kV` };
  if (voltage >= 115) return { risk: 3, phrase: `${voltage} kV` };
  return { risk: 4, phrase: `${voltage} kV (low)` };
}

function gradeSubstationKm(distance: number): { risk: number; phrase: string } {
  if (distance <= 5) return { risk: 2, phrase: `${distance} km to substation` };
  if (distance <= 15) return { risk: 3, phrase: `${distance} km to substation` };
  return { risk: 4, phrase: `${distance} km to substation (remote)` };
}

function gradeAcres(acres: number, basisNote: string): { risk: number; phrase: string } {
  if (acres >= 50) return { risk: 2, phrase: `${acres} acres${basisNote}` };
  if (acres >= 20) return { risk: 3, phrase: `${acres} acres${basisNote}` };
  return { risk: 4, phrase: `${acres} acres${basisNote} (constrained)` };
}

/**
 * Zoning classifier — free-form registry/claim strings graded into the legacy
 * bands (industrial favourable → risk 2; rezoning-likely classes → 4+). An
 * unclassifiable code still surfaces (transparency) at neutral risk with
 * HALF weight so it can never dominate classified signals.
 */
function gradeZoning(zoning: string): { risk: number; phrase: string; weight: number } {
  const z = zoning.toLowerCase();
  if (/industrial|manufactur|heavy|^i[-\s]?\d|^m[-\s]?\d/.test(z)) {
    return { risk: 2, phrase: `zoning ${zoning} (industrial)`, weight: 1 };
  }
  if (/agricultur|rural|ranch/.test(z)) {
    return { risk: 3, phrase: `zoning ${zoning} (agricultural, rezoning path typical)`, weight: 1 };
  }
  if (/residential|^r[-\s]?\d/.test(z)) {
    return { risk: 4.5, phrase: `zoning ${zoning} (residential, rezoning likely)`, weight: 1 };
  }
  return { risk: 3, phrase: `zoning ${zoning} (unclassified)`, weight: 0.5 };
}

function gradeFloodZone(zone: string): { risk: number; phrase: string } {
  if (/^X|^C|^D/i.test(zone)) return { risk: 2.5, phrase: `flood zone ${zone}` };
  return { risk: 4, phrase: `flood zone ${zone}` };
}

function gradeFloodRisk(band: string): { risk: number; phrase: string } | null {
  const b = band.toLowerCase();
  if (b === 'high') return { risk: 4.5, phrase: `flood risk ${band}` };
  if (b === 'medium') return { risk: 3.5, phrase: `flood risk ${band}` };
  if (b === 'low-medium') return { risk: 2.5, phrase: `flood risk ${band}` };
  if (b === 'low') return { risk: 2, phrase: `flood risk ${band}` };
  return null; // unknown band string — do not grade
}

/**
 * Wetlands grading. With the site acreage known the band is the legacy >10%
 * threshold; without it, an ASSUMED 25-acre absolute threshold approximates
 * 10% of a representative 250-acre campus (assumption documented here — the
 * per-fact phrase carries the basis used).
 */
function gradeWetlands(
  wetlandAcres: number,
  siteAcres: number | null,
): { risk: number; phrase: string } {
  if (siteAcres != null && siteAcres > 0) {
    const pct = (wetlandAcres / siteAcres) * 100;
    if (pct > 10) return { risk: 4, phrase: `wetlands ${wetlandAcres} ac (~${pct.toFixed(0)}% of site)` };
    if (wetlandAcres > 0) return { risk: 3, phrase: `wetlands ${wetlandAcres} ac (~${pct.toFixed(0)}% of site)` };
    return { risk: 2, phrase: 'no mapped wetlands' };
  }
  if (wetlandAcres >= 25) return { risk: 4, phrase: `wetlands ${wetlandAcres} ac (site size unknown; ≥25 ac threshold)` };
  if (wetlandAcres > 0) return { risk: 3, phrase: `wetlands ${wetlandAcres} ac (site size unknown)` };
  return { risk: 2, phrase: 'no mapped wetlands' };
}

function gradeStressLevel(level: string): { risk: number; phrase: string } {
  const s = level.toLowerCase();
  if (s.includes('low')) return { risk: 2, phrase: `water stress: ${level}` };
  if (s.includes('high') || s.includes('extreme')) return { risk: 4.5, phrase: `water stress: ${level}` };
  return { risk: 3, phrase: `water stress: ${level}` };
}

function gradeCapexPerKw(capex: number): { risk: number; phrase: string } {
  if (capex <= 6000) return { risk: 2, phrase: `capex $${capex}/kW` };
  if (capex <= 8500) return { risk: 3, phrase: `capex $${capex}/kW` };
  return { risk: 4.5, phrase: `capex $${capex}/kW (high)` };
}

/**
 * Thresholds are absolute $/MWh, so they are only meaningful against a STABLE
 * basis — and the basis must be REAL.
 *
 * A nominal LCOE carries an inflation wedge that grows with asset life: about
 * 22% on a 25-year CCGT and 35% on a 60-year nuclear plant at a 7.5% WACC.
 * Grading nominal figures against one fixed ladder therefore taxes long-lived
 * assets for being long-lived — it would mark an SMR down relative to a gas
 * peaker on nothing but tenor. Published cost benchmarks, which is what a
 * $100/$130 ladder implicitly references, are quoted in real terms.
 *
 * Callers pass `stamp.headline.lcoeRealUsdPerMwh` where present. Legacy stamps
 * predate the split and only carry the nominal figure; grading that preserves
 * their prior behaviour rather than silently re-banding old reports.
 */
function gradeLcoe(lcoe: number): { risk: number; phrase: string } {
  if (lcoe <= 100) return { risk: 2, phrase: `LCOE $${lcoe}/MWh` };
  if (lcoe <= 130) return { risk: 3, phrase: `LCOE $${lcoe}/MWh` };
  return { risk: 4, phrase: `LCOE $${lcoe}/MWh` };
}

/**
 * D14 — the deterministic FINANCIAL VERDICT BAND as a scoring signal in its own
 * right.
 *
 * `$/kW` and LCOE are COST inputs. A verdict band is the model's OUTPUT — it
 * already weighs those costs against revenue, financing, life and the hurdle.
 * Grading only the inputs is how an Unfinanceable site (NPV < 0) scored
 * low-risk on a flattering `$/kW`: cheap capital cost is not the same claim as
 * a financeable project, and on a staged build the flattering figure was often
 * an artifact of the campus basis in the first place.
 */
const FINANCIAL_BAND_GRADE: Record<'green' | 'amber' | 'red', { risk: number; label: string }> = {
  green: { risk: 2, label: 'Viable' },
  amber: { risk: 3.5, label: 'Conditional' },
  red: { risk: 5, label: 'Unfinanceable' },
};

/**
 * Weight of the band signal inside the financial pillar mean. Double the
 * per-input weight: the band is a JOINT statement about every input, so it must
 * not be outvoted 2:1 by the two cost inputs it already contains.
 */
const FINANCIAL_BAND_WEIGHT = 2;

/**
 * The risk an UNFINANCEABLE band floors the pillar at, applied after the mean.
 *
 * The weight alone is not sufficient: with two flattering cost inputs at risk 2
 * a red band still averages to ~3.5 ("moderate"), which is not an honest
 * reading of a model that says the project does not pay back. The floor makes
 * the band binding rather than merely influential — matching the ELCC floor's
 * logic, where a physical infeasibility cannot be averaged away by good
 * numbers elsewhere. 4.5 aligns with the top `gradeCapexPerKw` band so the two
 * scales stay comparable. A green/amber band applies NO floor (it is a signal,
 * not a guarantee — a good verdict must never LIFT the pillar past its inputs).
 */
const FINANCIAL_RED_BAND_FLOOR = 4.5;

/** Local finite-number guard (the stamp's headline fields are all nullable). */
function finiteNum(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/* ── Per-pillar evaluators ────────────────────────────────────────────── */

/**
 * POWER — grid headroom (load-relative vs `RESERVE_MARGIN_PCT`) + queue
 * duration + grid complexity, floored by the structured ELCC verdict.
 *
 * TRACK-A — the STRUCTURED firm-capacity (ELCC) verdict persisted on the
 * report (`report.firmCapacityVerdict.worstVerdict`) FLOORS the risk: an
 * ELCC-infeasible mix floors risk at 5, marginal at 3.5. This mirrors the
 * Stage-2a headline verdict cap (`capTierByFirmCapacity`) and only ever
 * WORSENS the pillar. The floor is applied AFTER the fact-mean in
 * `scorePillarFromReport`.
 */
function evaluatePower(
  report: SiteRagReport,
  evidence: SiteEvidenceDocument | null | undefined,
  facts: ReadonlyMap<string, CanonicalFact>,
  loadMW: number | null,
  livePowerAuthority?: PowerScoringAuthorityContext,
): PillarEvaluation {
  const md = reportText(report);
  const signals: FactSignal[] = [];
  const missing: PillarMissingFact[] = [];

  const service = resolveAllocatedServiceEvidence({ report, sed: evidence, live: livePowerAuthority, facilityLoadMW: loadMW });
  if (service) {
    const reserve = service.reserveMarginPct;
    const target = service.facilityDemandMW * (1 + reserve / 100);
    // Scheduled, source-stated service can establish a planning comparison,
    // never the strongest grade of measured utility availability.
    const risk = service.allocatedServiceMW < service.facilityDemandMW ? 4.5
      : service.allocatedServiceMW < target ? 3 : 2.5;
    signals.push({
      factKey: 'grid.allocatedServiceAdequacy', label: 'Allocated service vs facility demand',
      value: `${service.allocatedServiceMW} MW allocated / ${service.facilityDemandMW} MW facility demand`,
      provenance: 'derived', sourceLabel: `${service.documentName} · stated ${service.statedOn} [Ref ${service.referenceNumber}]`,
      weight: 2, risk, confidence: service.confidence,
      phrase: `Phase ${service.phaseId}: source-stated allocated service ${service.allocatedServiceMW} MW vs facility demand ${service.facilityDemandMW} MW; computed design load margin ${service.designLoadMarginMW} MW; scheduled service ${service.serviceDate}. Delivery and commissioning conditions remain; this is not measured grid headroom, ATC or energized service [derived]`,
    });
  }

  // Headroom — typed fact first, prose regex as labeled fallback. DOMINANT
  // driver (weight 2): it is the binding adequacy question for a firm load.
  const headroomFact = presentFact(facts, 'grid.headroomMw');
  const headroomNum = factNumber(headroomFact);
  if (headroomFact && headroomNum !== null) {
    const g = gradeHeadroom(headroomNum, loadMW);
    signals.push(signalFromFact({ fact: headroomFact, weight: 2, risk: g.risk, phrase: g.phrase }));
  } else {
    const utilityText = md.split(/(?<=[.!?])\s+|[;\n]/).filter(row => (
      !/\b(?:design\s+load\s+margin|source[- ]stated[^.!?]*design\s+comparison|(?:allocated\s+service|load[- ]service\s+allocation)[^.!?]*facility\s+demand)\b/i.test(row)
      && !/\b(?:no|not|without|unknown|unavailable|unmeasured)\b[^.!?]{0,80}\bheadroom\b/i.test(row)
      && !/\bheadroom\b[^.!?]{0,80}\b(?:unknown|unavailable|unmeasured|not\s+(?:measured|verified|available))\b/i.test(row)
    )).join('\n');
    const headroomProse = firstNumber(utilityText, [
      /headroom[^0-9]*(\d+(?:\.\d+)?)\s*MW/i,
      /(\d+(?:\.\d+)?)\s*MW[^.\n]{0,40}headroom/i,
    ]);
    if (headroomProse !== null) {
      const g = gradeHeadroom(headroomProse, loadMW);
      signals.push(signalFromProse({
        factKey: 'grid.headroomMw',
        label: 'Grid headroom',
        value: String(headroomProse),
        unit: 'MW',
        weight: 2,
        risk: g.risk,
        phrase: g.phrase,
      }));
    } else {
      missing.push({ factKey: 'grid.headroomMw', label: 'Grid headroom' });
    }
  }

  // Interconnection queue duration.
  const queueFact = presentFact(facts, 'grid.queueYears');
  const queueNum = factNumber(queueFact);
  if (queueFact && queueNum !== null) {
    const g = gradeQueueYears(queueNum);
    signals.push(signalFromFact({ fact: queueFact, weight: 1, risk: g.risk, phrase: g.phrase }));
  } else {
    const queueProse = siteLoadQueueYearsFromText(md);
    if (queueProse !== null) {
      const g = gradeQueueYears(queueProse);
      signals.push(signalFromProse({
        factKey: 'grid.queueYears',
        label: 'Interconnection queue (est.)',
        value: String(queueProse),
        unit: 'years',
        weight: 1,
        risk: g.risk,
        phrase: g.phrase,
      }));
    } else {
      missing.push({ factKey: 'grid.queueYears', label: 'Interconnection queue duration' });
    }
  }

  // Grid complexity — typed SED read. (The legacy read looked at
  // `evidence.grid.complexity` — the section WRAPPER, not `.data` — and
  // compared `=== 'high'` against the provider's `'High'`, so it never
  // fired. Read the real slot, case-insensitively; contributes only when
  // High, exactly the legacy intent.)
  const complexityRaw = readEvidencePath(evidence, 'grid.data.complexity')
    ?? readEvidencePath(evidence, 'grid.complexity');
  if (typeof complexityRaw === 'string' && complexityRaw.trim().toLowerCase() === 'high') {
    signals.push({
      factKey: 'grid.complexity',
      label: 'Grid complexity',
      value: complexityRaw.trim(),
      provenance: 'sed-slot',
      sourceLabel: 'Grid provider (HIFLD)',
      weight: 1,
      risk: 4,
      phrase: `grid complexity high ${PROVENANCE_TAG['sed-slot']}`,
      confidence: 1.0,
    });
  }

  // TRACK-A — ELCC firm-capacity floor (structured; applied after the mean).
  const floorVerdict = firmCapacityFloorVerdict(report);
  // ON-SITE GENERATION — the promoted fleet's own joint capacity screen. This
  // is the gap the grid-headroom signals above cannot see: a site can have
  // ample headroom and still have a promoted BTM fleet that fails its own N-k
  // criterion. An indeterminate screen is a missing fact, never a floor.
  const generationVerdict = generationScreenFloorVerdict(report);
  if (report.generationImpactProjection?.capacityAndResilience?.screenVerdict === 'indeterminate') {
    missing.push({
      factKey: 'generation.capacityScreen',
      label: 'On-site generation capacity screen',
    });
  }
  return {
    signals,
    missing,
    constraints: [],
    notes: [],
    ...(floorVerdict
      ? { elccFloor: { risk: FIRM_CAPACITY_RISK_FLOOR[floorVerdict], verdict: floorVerdict } }
      : {}),
    ...(generationVerdict
      ? {
          generationFloor: {
            risk: GENERATION_SCREEN_RISK_FLOOR[generationVerdict],
            label: `On-site generation capacity screen ${generationVerdict}`,
            kind: 'generation-screen-floor' as const,
          },
        }
      : {}),
  };
}

/** INTERCONNECTION — voltage class + substation distance (+ prose queue position). */
function evaluateInterconnection(
  report: SiteRagReport,
  facts: ReadonlyMap<string, CanonicalFact>,
): PillarEvaluation {
  const md = reportText(report);
  const signals: FactSignal[] = [];
  const missing: PillarMissingFact[] = [];

  const voltageFact = presentFact(facts, 'grid.voltageKv');
  const voltageNum = factNumber(voltageFact);
  if (voltageFact && voltageNum !== null) {
    const g = gradeVoltageKv(voltageNum);
    signals.push(signalFromFact({ fact: voltageFact, weight: 1, risk: g.risk, phrase: g.phrase }));
  } else {
    const v = firstNumber(md, [/(\d+(?:\.\d+)?)\s*kV/i]);
    if (v !== null) {
      const g = gradeVoltageKv(v);
      signals.push(signalFromProse({
        factKey: 'grid.voltageKv', label: 'Interconnection voltage', value: String(v), unit: 'kV',
        weight: 1, risk: g.risk, phrase: g.phrase,
      }));
    } else {
      missing.push({ factKey: 'grid.voltageKv', label: 'Interconnection voltage' });
    }
  }

  const distFact = presentFact(facts, 'substationDistKm');
  const distNum = factNumber(distFact);
  if (distFact && distNum !== null) {
    const g = gradeSubstationKm(distNum);
    signals.push(signalFromFact({ fact: distFact, weight: 1, risk: g.risk, phrase: g.phrase }));
  } else {
    const d = firstNumber(md, [/(\d+(?:\.\d+)?)\s*km[^.\n]{0,30}substation/i, /substation[^0-9]*(\d+(?:\.\d+)?)\s*km/i]);
    if (d !== null) {
      const g = gradeSubstationKm(d);
      signals.push(signalFromProse({
        factKey: 'substationDistKm', label: 'Nearest substation', value: String(d), unit: 'km',
        weight: 1, risk: g.risk, phrase: g.phrase,
      }));
    } else {
      missing.push({ factKey: 'substationDistKm', label: 'Nearest substation distance' });
    }
  }

  // Queue position — prose-only signal (no canonical fact yet).
  const queuePos = firstNumber(md, [/queue position[^0-9]*#?\s*(\d+)/i]);
  if (queuePos !== null && queuePos > 50) {
    signals.push(signalFromProse({
      factKey: 'grid.queuePosition', label: 'Queue position', value: `#${queuePos}`,
      weight: 1, risk: 4, phrase: `queue position #${queuePos}`,
    }));
  }

  return { signals, missing, constraints: [], notes: [] };
}

/** WATER — WRI Aqueduct stress level (typed) + permit/drought prose signals. */
function evaluateWater(
  report: SiteRagReport,
  evidence: SiteEvidenceDocument | null | undefined,
): PillarEvaluation {
  // A scenario's equipment duty cannot establish water rights or site stress.
  // Hear a frozen shortfall as adverse evidence; an adequate or unknown duty
  // must not improve the screen by counting its SED stress a second time.
  if (report.reportType === 'power-availability') {
    const duty = report.generationImpactProjection?.waterResource;
    return {
      signals: duty?.adequacy === 'fail' ? [{
        factKey: 'water.scenarioDutyAdequacy', label: 'Scenario water duty', value: 'fail',
        provenance: 'report-stamp', sourceLabel: 'Frozen generation scenario',
        weight: 1, risk: BLOCKING_RISK, confidence: 1,
        phrase: 'scenario water duty exceeds the declared available consumption [report]',
      }] : [],
      missing: [], constraints: [], notes: [],
    };
  }
  const screenText = waterScreenText(report);
  const md = report.reportType === 'feasibility'
    ? screenText
    : [reportText(report), screenText].join('\n');
  const signals: FactSignal[] = [];

  // Typed stress read: `waterStress.data.stressLevel` is the real slot. (The
  // legacy read looked at `evidence.climate.waterStress`, which does not
  // exist on the typed SED — kept as a last-resort duck-read for ancient
  // flat-shaped documents.)
  const stressRaw = readEvidencePath(evidence, 'waterStress.data.stressLevel')
    ?? readEvidencePath(evidence, 'waterStress.data.bwsLabel')
    ?? readEvidencePath(evidence, 'climate.waterStress');
  if (typeof stressRaw === 'string' && stressRaw.trim() !== '') {
    const g = gradeStressLevel(stressRaw.trim());
    signals.push({
      factKey: 'water.stressLevel',
      label: 'Water stress (WRI Aqueduct)',
      value: stressRaw.trim(),
      provenance: 'sed-slot',
      sourceLabel: 'WRI Aqueduct',
      weight: 1.5,
      risk: g.risk,
      phrase: `${g.phrase} ${PROVENANCE_TAG['sed-slot']}`,
      confidence: 1.0,
    });
  }

  // The rubric documents a 5.0 BLOCKING row for every pillar; nothing in these
  // ladders implemented it, so the worst any fact could score was 4.5 and a
  // legally-refused permit was indistinguishable from one merely thought
  // unlikely. A DENIED or REFUSED withdrawal permit is not a risk to weigh — the
  // site cannot lawfully take the water it needs, which is the definition of
  // blocking. "Unlikely" or "contested" remains a 4.5 concern.
  //
  // NOTE ON SCOPE: grid headroom is deliberately NOT graded blocking, even at
  // zero. A behind-the-meter project intentionally carries little or no grid
  // import, so a blanket rule there would manufacture NO-GO verdicts on sites
  // that are perfectly buildable. Power adequacy is already handled by the
  // firm-capacity cap and the source floor, which read the declared strategy.
  if (waterScreenHasDeniedPermit(report)
    || (report.reportType !== 'feasibility' && /withdrawal permit/i.test(md) && /denied|refused|blocked/i.test(md))) {
    signals.push(signalFromProse({
      factKey: 'water.withdrawalPermit', label: 'Withdrawal permit', value: 'denied',
      weight: 1, risk: BLOCKING_RISK, phrase: 'withdrawal permit denied — no lawful withdrawal path',
    }));
  } else if (/withdrawal permit/i.test(md) && /unlikely|contested|at risk/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'water.withdrawalPermit', label: 'Withdrawal permit', value: 'concern',
      weight: 1, risk: 4.5, phrase: 'withdrawal permit concern',
    }));
  } else if (/withdrawal permit/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'water.withdrawalPermit', label: 'Withdrawal permit', value: 'referenced',
      weight: 1, risk: 2.5, phrase: 'withdrawal permit referenced',
    }));
  }
  if (/drought/i.test(md) && /severe|extreme|high/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'water.drought', label: 'Drought risk', value: 'elevated',
      weight: 1, risk: 4, phrase: 'drought risk elevated',
    }));
  }

  return { signals, missing: [], constraints: [], notes: [] };
}

/** LAND — buildable/total acreage (typed, developer claims first) + zoning + slope. */
function evaluateLand(
  report: SiteRagReport,
  facts: ReadonlyMap<string, CanonicalFact>,
): PillarEvaluation {
  const md = reportText(report);
  const signals: FactSignal[] = [];
  const missing: PillarMissingFact[] = [];

  // Site size — the developer-stated BUILDABLE area is the honest capacity
  // basis when present (X-SED-6); else the surveyed/parcel site area.
  const buildableFact = presentFact(facts, 'siteArea.buildableAcres');
  const buildableNum = factNumber(buildableFact);
  const totalFact = presentFact(facts, 'siteArea.acres');
  const totalNum = factNumber(totalFact);
  if (buildableFact && buildableNum !== null) {
    const g = gradeAcres(buildableNum, ' buildable (developer-stated)');
    signals.push(signalFromFact({ fact: buildableFact, weight: 1, risk: g.risk, phrase: g.phrase }));
  } else if (totalFact && totalNum !== null) {
    const g = gradeAcres(totalNum, '');
    signals.push(signalFromFact({ fact: totalFact, weight: 1, risk: g.risk, phrase: g.phrase }));
  } else {
    missing.push({ factKey: 'siteArea.acres', label: 'Site area' });
  }

  // Zoning — typed fact first (registry/claims), else the legacy prose cues.
  const zoningFact = presentFact(facts, 'parcel.zoning');
  const zoningStr = factString(zoningFact);
  if (zoningFact && zoningStr !== null) {
    const g = gradeZoning(zoningStr);
    signals.push(signalFromFact({ fact: zoningFact, weight: g.weight, risk: g.risk, phrase: g.phrase }));
  } else if (/industrial zoning/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'parcel.zoning', label: 'Zoning', value: 'industrial',
      weight: 1, risk: 2, phrase: 'industrial zoning',
    }));
  } else if (/rezon|variance required/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'parcel.zoning', label: 'Zoning', value: 'rezoning likely',
      weight: 1, risk: 4, phrase: 'rezoning likely',
    }));
  } else {
    missing.push({ factKey: 'parcel.zoning', label: 'Zoning' });
  }

  // Slope — prose-only signal (no typed slot on the SED today).
  const slope = firstNumber(md, [/(\d+(?:\.\d+)?)\s*%\s*slope/i]);
  if (slope !== null && slope > 8) {
    signals.push(signalFromProse({
      factKey: 'land.slopePct', label: 'Slope', value: `${slope}%`,
      weight: 1, risk: 4, phrase: `slope ${slope}%`,
    }));
  }

  // ON-SITE GENERATION — does the promoted fit actually CONTAIN on this parcel?
  // The acreage and zoning signals above cannot answer that: a large, well-zoned
  // site whose promoted layout overruns its boundary still scores well on both.
  const containment = generationContainmentFailed(report);
  return {
    signals,
    missing,
    constraints: [],
    notes: [],
    ...(containment
      ? {
          generationFloor: {
            risk: GENERATION_CONTAINMENT_RISK_FLOOR,
            label: containment === 'acreage'
              ? 'Promoted generation footprint exceeds net-buildable area'
              : 'Promoted generation geometry falls outside the parcel boundary',
            kind: 'generation-containment-floor' as const,
          },
        }
      : {}),
  };
}

/** ENVIRONMENTAL — flood zone/risk + wetlands + critical habitat (typed) + prose cues. */
function evaluateEnvironmental(
  report: SiteRagReport,
  evidence: SiteEvidenceDocument | null | undefined,
  facts: ReadonlyMap<string, CanonicalFact>,
): PillarEvaluation {
  const md = reportText(report);
  const signals: FactSignal[] = [];
  const missing: PillarMissingFact[] = [];

  const zoneFact = presentFact(facts, 'flood.zone');
  const zoneStr = factString(zoneFact);
  if (zoneFact && zoneStr !== null) {
    const g = gradeFloodZone(zoneStr);
    signals.push(signalFromFact({ fact: zoneFact, weight: 1, risk: g.risk, phrase: g.phrase }));
  } else {
    missing.push({ factKey: 'flood.zone', label: 'FEMA flood zone' });
  }

  // Flood risk band — SECONDARY confirmation of the zone (half weight so the
  // correlated pair never double-counts a full flood signal).
  const riskFact = presentFact(facts, 'flood.risk');
  const riskStr = factString(riskFact);
  if (riskFact && riskStr !== null) {
    const g = gradeFloodRisk(riskStr);
    if (g) signals.push(signalFromFact({ fact: riskFact, weight: 0.5, risk: g.risk, phrase: g.phrase }));
  }

  // Wetlands — typed acreage, graded against the site size when known.
  const sourceFeatureTotals = (evidence?.environmental?.data?.wetlandAcreageBasis
    ?? report.datasets?.environmental?.wetlandAcreageBasis) === 'source-feature-total';
  const wetlandsFact = sourceFeatureTotals ? null : presentFact(facts, 'wetlands.acres');
  const wetlandsNum = factNumber(wetlandsFact);
  if (wetlandsFact && wetlandsNum !== null) {
    const siteAcres = factNumber(presentFact(facts, 'siteArea.acres'));
    const g = gradeWetlands(wetlandsNum, siteAcres);
    signals.push(signalFromFact({ fact: wetlandsFact, weight: 1, risk: g.risk, phrase: g.phrase }));
  } else if (!sourceFeatureTotals && /wetland/i.test(md) && /(\d+(?:\.\d+)?)\s*%/i.test(md)) {
    const pct = firstNumber(md, [/wetland[^0-9]*(\d+(?:\.\d+)?)\s*%/i]);
    if (pct != null && pct > 10) {
      signals.push(signalFromProse({
        factKey: 'wetlands.acres', label: 'Wetlands', value: `~${pct}%`,
        weight: 1, risk: 4, phrase: `wetlands ~${pct}%`,
      }));
    }
  } else {
    missing.push({ factKey: 'wetlands.acres', label: 'Wetlands acreage' });
  }

  // Critical habitat — a typed FALSE is a positive finding (verified clear).
  const habitatFact = presentFact(facts, 'env.hasCriticalHabitat');
  if (habitatFact && typeof habitatFact.value === 'boolean') {
    const has = habitatFact.value;
    signals.push(signalFromFact({
      fact: habitatFact,
      weight: 1,
      risk: has ? 4 : 2,
      phrase: has ? 'critical habitat mapped on site' : 'no critical habitat mapped',
      valueOverride: has ? 'yes' : 'no',
    }));
  } else if (/species|ESA|critical habitat/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'env.hasCriticalHabitat', label: 'Critical habitat (USFWS)', value: 'flagged in prose',
      weight: 1, risk: 3.5, phrase: 'species/habitat flags',
    }));
  } else {
    missing.push({ factKey: 'env.hasCriticalHabitat', label: 'Critical habitat flag' });
  }

  // Air non-attainment — prose-only signal.
  if (/non-attainment|nonattainment/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'env.airNonAttainment', label: 'Air quality', value: 'non-attainment',
      weight: 1, risk: 4, phrase: 'air non-attainment',
    }));
  }

  return { signals, missing, constraints: [], notes: [] };
}

/** REGULATORY — zoning posture (typed) + permit-lead / sentiment prose cues. */
function evaluateRegulatory(
  report: SiteRagReport,
  facts: ReadonlyMap<string, CanonicalFact>,
): PillarEvaluation {
  const md = reportText(report);
  const signals: FactSignal[] = [];
  const missing: PillarMissingFact[] = [];

  const zoningFact = presentFact(facts, 'parcel.zoning');
  const zoningStr = factString(zoningFact);
  if (zoningFact && zoningStr !== null) {
    const g = gradeZoning(zoningStr);
    signals.push(signalFromFact({ fact: zoningFact, weight: g.weight, risk: g.risk, phrase: g.phrase }));
  } else {
    missing.push({ factKey: 'parcel.zoning', label: 'Zoning' });
  }

  const leadTime = firstNumber(md, [/permit[^0-9]*(\d+(?:\.\d+)?)\s*(?:months?|mo)/i]);
  if (leadTime !== null) {
    const g = leadTime <= 12
      ? { risk: 2, phrase: `permit lead ~${leadTime}mo` }
      : leadTime <= 24
        ? { risk: 3, phrase: `permit lead ~${leadTime}mo` }
        : { risk: 4.5, phrase: `permit lead ~${leadTime}mo` };
    signals.push(signalFromProse({
      factKey: 'regulatory.permitLeadMonths', label: 'Permit lead time', value: `${leadTime} mo`,
      weight: 1, risk: g.risk, phrase: g.phrase,
    }));
  }
  if (/opposition|moratorium|litigation/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'regulatory.opposition', label: 'Opposition signals', value: 'present',
      weight: 1, risk: 4, phrase: 'opposition signals',
    }));
  }
  if (/supportive|expedited|streamlined/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'regulatory.supportive', label: 'Jurisdiction posture', value: 'supportive',
      weight: 1, risk: 2.5, phrase: 'supportive jurisdiction',
    }));
  }

  return { signals, missing, constraints: [], notes: [] };
}

/** SUPPLY CHAIN — prose-only signals (no canonical facts declared yet). */
function evaluateSupplyChain(report: SiteRagReport): PillarEvaluation {
  const md = reportText(report);
  const signals: FactSignal[] = [];

  const leadWeeks = firstNumber(md, [/lead[^0-9]*(\d+(?:\.\d+)?)\s*(?:weeks?|months?)/i]);
  if (leadWeeks !== null) {
    const weeks = /month/i.test(md) ? leadWeeks * 4 : leadWeeks;
    const g = weeks <= 26
      ? { risk: 2.5, phrase: `lead time ~${weeks}w` }
      : weeks <= 52
        ? { risk: 3.5, phrase: `lead time ~${weeks}w` }
        : { risk: 4.5, phrase: `lead time ~${weeks}w` };
    signals.push(signalFromProse({
      factKey: 'supplyChain.leadWeeks', label: 'Equipment lead time', value: `${weeks} w`,
      weight: 1, risk: g.risk, phrase: g.phrase,
    }));
  }
  if (/single.?source|sole source/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'supplyChain.singleSource', label: 'Sourcing', value: 'single-source',
      weight: 1, risk: 4, phrase: 'single-source risk',
    }));
  }
  if (/tariff|Section 301|trade/i.test(md)) {
    signals.push(signalFromProse({
      factKey: 'supplyChain.tariff', label: 'Tariff exposure', value: 'flagged',
      weight: 1, risk: 3.5, phrase: 'tariff exposure',
    }));
  }

  return { signals, missing: [], constraints: [], notes: [] };
}

/**
 * Part C SP4 (S2) — null-safe read of the LCOE (USD/MWh) that drives the
 * financial pillar, derived from the ONE canonical corrected-LCOE surface
 * (`resolveReportCorrectedLcoe`) that the report renderers (BTM overview table +
 * LCOE chart) also read. The surface buckets by technology and keeps the lowest
 * figure per bucket (persisted-first, else live compute from the gen-time
 * intent); the pillar takes the WORST (highest) corrected figure across buckets
 * (`worstCorrectedLcoe`) so it reflects the costliest path the site would
 * actually build. Falls back to the legacy `lcoeDetailedAnalysis.lcoe` field
 * for pre-snapshot reports whose intent does not resolve to a corrected
 * surface. Returns `null` (never NaN/throw) when neither resolves.
 */
function resolveFinancialLcoe(report: SiteRagReport): number | null {
  // Single source of truth — the corrected per-tech surface the report renders.
  const surface = resolveReportCorrectedLcoe(report);
  const worst = worstCorrectedLcoe(surface);
  if (worst != null) return worst;
  // Legacy fallback — the previously-(non-)resolving detailed-analysis path.
  const rec = report as unknown as Record<string, unknown>;
  const legacy = getReportFieldValue(rec, 'lcoeDetailedAnalysis.lcoe') as number | undefined;
  return typeof legacy === 'number' && Number.isFinite(legacy) ? legacy : null;
}

/**
 * FINANCIAL — the deterministic VERDICT BAND (when stamped) + CAPEX $/kW +
 * LCOE.
 *
 * D14 (review 2026-08-19) — this pillar used to grade ONLY `$/kW` read from
 * `financialAssumptionsAtGen` and a pathway-engine LCOE, and never looked at
 * `report.financialAnalysis` at all. Two consequences, both live:
 *
 *   1. `financialAssumptionsAtGen.capexPerKwUsd` is the UNCORRECTED CAMPUS
 *      basis — total programme capital ÷ ULTIMATE nameplate. On a staged build
 *      that is the $137/kW-where-the-priced-phase-is-$800/kW error: the pillar
 *      graded a number that describes no buildable thing. The stamp's
 *      `headline.capexPerKwUsd` is already corrected to the PRICED-PHASE basis
 *      (`deriveCapexPerKwFromPricedPhases`), so it is the figure that can be
 *      graded — and it is preferred here whenever a stamp exists.
 *   2. Grading inputs only meant an UNFINANCEABLE site scored LOW-RISK on a
 *      flattering `$/kW`. The band is the model's own output over every input;
 *      it now enters as a double-weighted signal AND, when red, as a floor.
 *
 * Fallbacks are unchanged for reports with no stamp (legacy + non-financial
 * report types), so nothing pre-stamp is regressed.
 */
function evaluateFinancial(
  report: SiteRagReport,
  facts: ReadonlyMap<string, CanonicalFact>,
  loadMW: number | null,
): PillarEvaluation {
  const signals: FactSignal[] = [];
  const missing: PillarMissingFact[] = [];
  const notes: string[] = [];
  const stamp = report.financialAnalysis;

  // ── The deterministic verdict band + viability score. ────────────────────
  // A WITHHELD band (null) is NOT graded — the engine declined to band these
  // inputs, and imputing a risk from a verdict that does not exist is exactly
  // the fabricated-downgrade this scorer refuses everywhere else. It is
  // recorded as a GAP instead.
  let financialFloor: PillarEvaluation['financialFloor'];
  if (stamp) {
    const band = stamp.band;
    if (band === 'green' || band === 'amber' || band === 'red') {
      const g = FINANCIAL_BAND_GRADE[band];
      const score = finiteNum(stamp.viabilityScore) ? Math.round(stamp.viabilityScore) : null;
      signals.push({
        factKey: 'finance.verdictBand',
        label: 'Financial verdict (engine band)',
        value: score != null ? `${g.label} · ${score}/100` : g.label,
        provenance: 'report-stamp',
        sourceLabel: 'projectFinance · finDecision stamp',
        weight: FINANCIAL_BAND_WEIGHT,
        risk: g.risk,
        phrase: `financial verdict ${g.label}${score != null ? ` (score ${score}/100)` : ''} ${PROVENANCE_TAG['report-stamp']}`,
        confidence: 1.0,
      });
      if (band === 'red') {
        financialFloor = {
          risk: FINANCIAL_RED_BAND_FLOOR,
          label: 'Unfinanceable financial verdict',
        };
      }
    } else {
      missing.push({ factKey: 'finance.verdictBand', label: 'Financial verdict (withheld)' });
      notes.push(
        `financial verdict withheld (${stamp.verdictWithheldReason ?? 'not gradeable'}) — band excluded from the pillar mean`,
      );
    }
  }

  // CAPEX $/kW — the ENGINE STAMP's corrected priced-phase figure first; then
  // the report-stamped assumptions (the legacy read — CAMPUS basis, labeled as
  // such); then a total-CAPEX fact + known load.
  const fa = report.financialAssumptionsAtGen ?? report.financialAssumptionsOverride;
  const rec = report as unknown as Record<string, unknown>;
  const stampCapexPerKw = stamp ? stamp.headline.capexPerKwUsd : null;
  const capexPerKw = fa?.capexPerKwUsd
    ?? (getReportFieldValue(rec, 'financialAssumptions.capexPerKwUsd') as number | undefined);
  const capexTotalFact = presentFact(facts, 'finance.capexTotalUsd');
  const capexTotalNum = factNumber(capexTotalFact);
  if (finiteNum(stampCapexPerKw)) {
    const g = gradeCapexPerKw(Math.round(stampCapexPerKw));
    signals.push({
      factKey: 'finance.capexPerKwUsd',
      label: 'CAPEX per kW',
      value: `$${Math.round(stampCapexPerKw)}/kW`,
      unit: 'USD/kW',
      provenance: 'report-stamp',
      sourceLabel: 'Financial engine stamp (priced-phase basis)',
      weight: 1,
      risk: g.risk,
      phrase: `${g.phrase} (priced-phase basis) ${PROVENANCE_TAG['report-stamp']}`,
      confidence: 1.0,
    });
  } else if (typeof capexPerKw === 'number' && Number.isFinite(capexPerKw)) {
    const g = gradeCapexPerKw(capexPerKw);
    signals.push({
      factKey: 'finance.capexPerKwUsd',
      label: 'CAPEX per kW',
      value: `$${capexPerKw}/kW`,
      unit: 'USD/kW',
      provenance: 'report-stamp',
      sourceLabel: 'Financial assumptions at generation (campus basis)',
      weight: 1,
      risk: g.risk,
      phrase: `${g.phrase} (campus basis) ${PROVENANCE_TAG['report-stamp']}`,
      confidence: 1.0,
    });
  } else if (capexTotalFact && capexTotalNum !== null && loadMW !== null) {
    // Derived $/kW = total CAPEX ÷ (facility load MW × 1000 kW/MW).
    const derivedPerKw = Math.round(capexTotalNum / (loadMW * 1000));
    const g = gradeCapexPerKw(derivedPerKw);
    signals.push({
      factKey: 'finance.capexTotalUsd',
      label: 'Total CAPEX',
      value: `$${derivedPerKw}/kW (derived)`,
      unit: 'USD/kW',
      provenance: 'derived',
      sourceLabel: `${capexTotalFact.sources[0]?.label ?? 'Financial assumptions'} ÷ ${loadMW.toFixed(0)} MW`,
      weight: 1,
      risk: g.risk,
      phrase: `${g.phrase} (derived from total CAPEX ÷ load) ${PROVENANCE_TAG.derived}`,
      confidence: STATUS_CONFIDENCE[capexTotalFact.status as Exclude<FactStatus, 'absent'>] ?? 0.8,
    });
  } else {
    missing.push({ factKey: 'finance.capexTotalUsd', label: 'CAPEX' });
  }

  // LCOE — the ENGINE STAMP's headline first (the same LCOE the report's own
  // verdict and headline metrics were computed on), then the corrected report
  // surface (single source of truth with the rendered table/chart), then the
  // SED's computed blended-LCOE anchor.
  // REAL where the stamp has it — see gradeLcoe on why the ladder needs a
  // basis-stable input. Falls back to the nominal figure for stamps written
  // before the two bases were split, which keeps legacy reports scoring as
  // they always did.
  const stampLcoe = stamp
    ? (finiteNum(stamp.headline.lcoeRealUsdPerMwh)
        ? stamp.headline.lcoeRealUsdPerMwh
        : stamp.headline.lcoeUsdPerMwh)
    : null;
  // Only resolve the corrected surface when the stamp cannot answer — that
  // resolver walks the whole per-technology LCOE surface.
  const lcoe = finiteNum(stampLcoe) ? null : resolveFinancialLcoe(report);
  const lcoeFact = presentFact(facts, 'lcoe.blendedPerMwhUsd');
  const lcoeFactNum = factNumber(lcoeFact);
  if (finiteNum(stampLcoe)) {
    const rounded = Math.round(stampLcoe);
    const g = gradeLcoe(rounded);
    signals.push({
      factKey: 'lcoe.projectFinancePerMwhUsd',
      label: stamp != null && finiteNum(stamp.headline.lcoeRealUsdPerMwh)
        ? 'LCOE (project-finance engine, real)'
        : 'LCOE (project-finance engine)',
      value: `$${rounded}/MWh`,
      unit: 'USD/MWh',
      provenance: 'report-stamp',
      sourceLabel: 'Financial engine stamp (headline LCOE)',
      weight: 1,
      risk: g.risk,
      phrase: `${g.phrase} ${PROVENANCE_TAG['report-stamp']}`,
      confidence: 1.0,
    });
  } else if (typeof lcoe === 'number' && Number.isFinite(lcoe)) {
    const g = gradeLcoe(lcoe);
    signals.push({
      factKey: 'lcoe.correctedWorstPerMwhUsd',
      label: 'Corrected LCOE (worst bucket)',
      value: `$${lcoe}/MWh`,
      unit: 'USD/MWh',
      provenance: 'report-stamp',
      sourceLabel: 'Corrected LCOE surface (pathwayLcoeAtGen)',
      weight: 1,
      risk: g.risk,
      phrase: `${g.phrase} ${PROVENANCE_TAG['report-stamp']}`,
      confidence: 1.0,
    });
  } else if (lcoeFact && lcoeFactNum !== null) {
    const g = gradeLcoe(lcoeFactNum);
    signals.push(signalFromFact({ fact: lcoeFact, weight: 1, risk: g.risk, phrase: g.phrase }));
  } else {
    missing.push({ factKey: 'lcoe.blendedPerMwhUsd', label: 'LCOE' });
  }

  return {
    signals,
    missing,
    constraints: [],
    notes,
    ...(financialFloor ? { financialFloor } : {}),
  };
}

/** SOCIAL — dated local opinion about selected technologies, bound to the
 * report's source SED. General prose and other-project opposition are context. */
function evaluateSocial(report: SiteRagReport, evidence?: SiteEvidenceDocument | null): PillarEvaluation {
  const section = evidence?.publicSentiment;
  const sameSource = !!report.sedId && report.sedId === evidence?.id
    && section?.queryContext?.fingerprint === report.publicSentimentMeta?.queryContext?.fingerprint;
  const entries = sameSource && report.publicSentimentStatus !== 'unavailable'
    ? qualifiedLocalSentimentEntries(section) : [];
  const assessment = assessPublicSentiment(entries);
  if (assessment.supportIndex === undefined || !assessment.meanConfidence) return {
    signals: [], missing: [{ factKey: 'social.localStance', label: 'Dated local public stance for the development intent' }],
    constraints: [], notes: ['No qualified local opinion measurement; contextual records do not establish support or opposition.'],
  };
  return { signals: [{ factKey: 'social.localSupportIndex', label: 'Dated local public stance',
    value: `${assessment.supportIndex}/100`, provenance: 'sed-slot', sourceLabel: 'SED public sentiment — dated local explicit stance',
    weight: 1, risk: 5 - assessment.supportIndex / 25, confidence: assessment.meanConfidence,
    phrase: `${assessment.localObservationCount} dated local observations: support index ${assessment.supportIndex}/100; not a population survey`,
  }], missing: [], constraints: [], notes: [] };
}

/* ── Pillar evaluation → contribution ─────────────────────────────────── */

function evaluatePillar(
  pillarId: PillarId,
  report: SiteRagReport,
  evidence: SiteEvidenceDocument | null | undefined,
  facts: ReadonlyMap<string, CanonicalFact>,
  loadMW: number | null,
  livePowerAuthority?: PowerScoringAuthorityContext,
): PillarEvaluation {
  switch (pillarId) {
    case 'power': return evaluatePower(report, evidence, facts, loadMW, livePowerAuthority);
    case 'interconnection': return evaluateInterconnection(report, facts);
    case 'water': return evaluateWater(report, evidence);
    case 'land': return evaluateLand(report, facts);
    case 'environmental': return evaluateEnvironmental(report, evidence, facts);
    case 'regulatory': return evaluateRegulatory(report, facts);
    case 'supplyChain': return evaluateSupplyChain(report);
    case 'financial': return evaluateFinancial(report, facts, loadMW);
    case 'social': return evaluateSocial(report, evidence);
    default:
      return { signals: [], missing: [], constraints: [], notes: [] };
  }
}

export function scorePillarFromReport(
  pillarId: PillarId,
  reportTypeId: ReportTypeId,
  report: SiteRagReport,
  evidence?: SiteEvidenceDocument | null,
  /**
   * The site's own facility LOAD (MW) — used by the power pillar to grade grid
   * headroom relative to demand + reserve margin, and by the financial pillar
   * to derive $/kW from a total-CAPEX fact. Optional: when omitted the power
   * pillar falls back to legacy absolute headroom thresholds.
   */
  loadMW?: number | null,
  /** Optional current owner-loaded site authority for source-bound service planning. */
  livePowerAuthority?: PowerScoringAuthorityContext,
): PillarScoreContribution {
  if (pillarId === 'evidenceQuality') {
    return {
      score: 0,
      confidence: 0.5,
      rationale: 'Y2 stub — filled by dataConfidenceCalculator in Part B',
      citations: [],
      reportId: report.id,
      reportTypeId,
    };
  }

  const load = typeof loadMW === 'number' && Number.isFinite(loadMW) && loadMW > 0 ? loadMW : null;
  // Resolve the typed scoring facts for this (report, SED) pair — same
  // descriptor table + tier reconciliation as the report-grounding layer.
  const facts = buildScoringFactSlice({ sed: evidence ?? null, report });

  const evaluation = evaluatePillar(pillarId, report, evidence, facts, load, livePowerAuthority);
  const { signals, missing } = evaluation;

  const factContributions: PillarFactContribution[] = signals.map((s) => ({
    factKey: s.factKey,
    label: s.label,
    value: s.value,
    unit: s.unit,
    factProvenance: s.provenance,
    sourceLabel: s.sourceLabel,
    weight: s.weight,
    score: riskToBenefit(s.risk),
    note: s.phrase,
  }));

  // NO signal at all → a signal-less contribution. Never a silent default
  // score: `composePillarScore` EXCLUDES it from the mean, and a pillar whose
  // every contribution is signal-less composes to MISSING ("Awaiting Data").
  if (signals.length === 0) {
    return {
      score: riskToBenefit(3), // neutral placeholder — excluded from the mean
      confidence: 0,
      rationale: `no scoring facts resolved (${missing.length} gap${missing.length === 1 ? '' : 's'}${missing.length > 0 ? `: ${missing.map((m) => m.factKey).join(', ')}` : ''})`,
      citations: citationsFromFindings(report),
      reportId: report.id,
      reportTypeId,
      noScoringSignal: true,
      factContributions: [],
      ...(missing.length > 0 ? { missingFacts: missing } : {}),
    };
  }

  // Weighted risk mean over the RESOLVED signals only (a missing fact is
  // excluded — never imputed as a red floor or a silent amber midpoint).
  let riskSum = 0;
  let weightSum = 0;
  let confSum = 0;
  for (const s of signals) {
    riskSum += s.risk * s.weight;
    weightSum += s.weight;
    confSum += s.confidence * s.weight;
  }
  let risk = clamp(riskSum / weightSum, 1, 5);
  const basisConfidence = clamp(confSum / weightSum, 0, 1);

  const parts: string[] = signals.map((s) => s.phrase);
  const constraints: PillarConstraint[] = [...evaluation.constraints];

  // TRACK-A — ELCC firm-capacity floor (power pillar): applied AFTER the
  // fact mean; only ever WORSENS. Part C SP2 — a BINDING floor (floor > the
  // pre-floor risk) also records a structured constraint so the deep dive can
  // show "what moved this score" as a chip; a non-binding floor records
  // nothing (the score was unchanged by the verdict).
  if (evaluation.elccFloor) {
    const { risk: floor, verdict } = evaluation.elccFloor;
    if (floor > risk) {
      risk = floor;
      parts.push(`ELCC firm-capacity ${verdict} → risk floored at ${floor}`);
      constraints.push({
        kind: 'firm-capacity-floor',
        label: `ELCC firm-capacity ${verdict}`,
        movedTo: floor,
      });
    } else {
      parts.push(`ELCC firm-capacity ${verdict} (already ≤ headroom risk)`);
    }
  }

  // ON-SITE GENERATION floor (power + land): identical mechanism to the ELCC
  // floor above, and composed through the SAME `> risk` comparison — so when
  // both fire the worse one binds rather than the two stacking. Only ever
  // worsens; a non-binding floor records a note and no chip.
  if (evaluation.generationFloor) {
    const { risk: floor, label, kind } = evaluation.generationFloor;
    if (floor > risk) {
      risk = floor;
      parts.push(`${label} → risk floored at ${floor}`);
      constraints.push({ kind, label, movedTo: floor });
    } else {
      parts.push(`${label} (already ≤ pillar risk)`);
    }
  }

  // D14 — FINANCIAL verdict-band floor. Same `> risk` mechanism as the two
  // floors above (worst binds; never lifts), and it now records its own
  // `financial-band-floor` chip so the deep-dive names WHY the pillar moved
  // rather than leaving the reader to infer it from the rationale string.
  if (evaluation.financialFloor) {
    const { risk: floor, label } = evaluation.financialFloor;
    if (floor > risk) {
      risk = floor;
      parts.push(`${label} → risk floored at ${floor}`);
      constraints.push({ kind: 'financial-band-floor', label, movedTo: floor });
    } else {
      parts.push(`${label} (already ≤ pillar risk)`);
    }
  }
  parts.push(...evaluation.notes);

  return {
    score: riskToBenefit(risk),
    // Report QA confidence × fact-basis confidence: a pillar riding on prose
    // heuristics (0.5) or developer claims (0.6) weighs less in the composite
    // than one grounded in verified provider slots (1.0).
    confidence: clamp(confidenceFromReport(report) * basisConfidence, 0, 1),
    rationale: parts.join('; '),
    citations: citationsFromFindings(report),
    reportId: report.id,
    reportTypeId,
    // Part C SP2 — only attach when one or more BINDING constraints fired.
    ...(constraints.length > 0 ? { constraints } : {}),
    factContributions,
    ...(missing.length > 0 ? { missingFacts: missing } : {}),
  };
}

/** True when a contribution carries a real scoring signal (legacy ⇒ true). */
function hasScoringSignal(c: PillarScoreContribution): boolean {
  return c.noScoringSignal !== true;
}

export function composePillarScore(
  siteId: string,
  pillarId: PillarId,
  contributions: PillarScoreContribution[],
): PillarCompositeScore {
  void siteId;
  const weight = PILLARS[pillarId].weight;
  if (contributions.length === 0) {
    return {
      pillarId,
      score: PORTFOLIO_CONFIG.WORST_CASE_SCORE,
      confidence: 0,
      weight,
      rationale: 'No source reports available',
      citations: [],
      sourceReportIds: [],
      missing: true,
    };
  }

  // L-tier rebuild — contributions with NO scoring signal are EXCLUDED from
  // the score mean (their gaps still aggregate). All-signal-less composes to
  // MISSING: the deep dive renders "Awaiting Data", never a fake amber-3.
  const scored = contributions.filter(hasScoringSignal);

  // Aggregate the per-fact provenance + gaps across all contributions.
  const factContributions: PillarFactContribution[] = [];
  const seenFacts = new Set<string>();
  for (const c of contributions) {
    for (const fc of c.factContributions ?? []) {
      const key = `${fc.factKey}:${fc.value}`;
      if (!seenFacts.has(key)) {
        seenFacts.add(key);
        factContributions.push(fc);
      }
    }
  }
  const resolvedFactKeys = new Set(factContributions.map((fc) => fc.factKey));
  const missingFacts: PillarMissingFact[] = [];
  const seenMissing = new Set<string>();
  for (const c of contributions) {
    for (const mf of c.missingFacts ?? []) {
      // A fact resolved by ANY sibling contribution is not a pillar-level gap.
      if (resolvedFactKeys.has(mf.factKey) || seenMissing.has(mf.factKey)) continue;
      seenMissing.add(mf.factKey);
      missingFacts.push(mf);
    }
  }
  const factExtras = {
    ...(factContributions.length > 0 ? { factContributions: factContributions.slice(0, 16) } : {}),
    ...(missingFacts.length > 0 ? { missingFacts } : {}),
  };

  const sourceReportIds: string[] = [];
  for (const c of contributions) {
    if (!sourceReportIds.includes(c.reportId)) sourceReportIds.push(c.reportId);
  }

  if (scored.length === 0) {
    // FIX 2026-09-05 — provenance decides whether this is UNREADABLE evidence or
    // simply NO evidence, and the two must score differently.
    //
    // `sourceReports[0]` is the pillar's DEDICATED report by registry
    // convention (power -> 'power-availability', financial -> 'financial',
    // land -> 'test-fit'); anything after it is a FALLBACK that also happens to
    // carry some of the pillar's evidence. When the dedicated report ran and
    // produced nothing gradeable, the site must not profit from holding
    // evidence nobody can read — that is the `unreadable` branch in
    // `aggregateBenefitComposite`. But when only a FALLBACK contributed and it
    // had nothing to say, the pillar is genuinely unmeasured; scoring it at
    // UNREADABLE_PILLAR_SCORE would inject a synthetic 3.0 at full weight and
    // RAISE the composite of any site whose real evidence sits below it —
    // which is the exact composite-raising defect that branch was written to
    // prevent. Stage 5.2 made this live by adding 'feasibility' to the power
    // pillar's sources at weight 0.21, the heaviest in the frame.
    const dedicatedSource = PILLARS[pillarId]?.sourceReports?.[0];
    const dedicatedSourceUnreadable = dedicatedSource != null
      && contributions.some(c => c.reportTypeId === dedicatedSource);
    return {
      pillarId,
      score: PORTFOLIO_CONFIG.WORST_CASE_SCORE,
      confidence: 0,
      weight,
      rationale: dedicatedSourceUnreadable
        ? `No scoring facts resolved from ${contributions.length} source report(s); pillar withheld pending evidence`
        : `No ${pillarId} evidence in the ${contributions.length} contributing report(s); pillar unmeasured`,
      citations: [],
      sourceReportIds,
      missing: true,
      dedicatedSourceUnreadable,
      ...factExtras,
    };
  }

  let weightedSum = 0;
  let confSum = 0;
  const rationales: string[] = [];
  const citations = new Set<string | import('../../types/portfolioComposite').Citation>();
  // Part C SP2 — collect the structured binding constraints from every
  // contribution so they surface as deep-dive chips. Dedup by kind+label so a
  // multi-report pillar does not show the same floor twice.
  const constraints: PillarConstraint[] = [];
  const seenConstraints = new Set<string>();

  for (const c of scored) {
    const w = c.confidence > 0 ? c.confidence : 0.01;
    weightedSum += c.score * w;
    confSum += w;
    rationales.push(`[${c.reportTypeId}] ${c.rationale}`);
    for (const cite of c.citations) citations.add(cite);
    for (const con of c.constraints ?? []) {
      const key = `${con.kind}:${con.label}`;
      if (!seenConstraints.has(key)) {
        seenConstraints.add(key);
        constraints.push(con);
      }
    }
  }

  const score = confSum > 0 ? weightedSum / confSum : PORTFOLIO_CONFIG.WORST_CASE_SCORE;
  const confidence = confSum > 0
    ? scored.reduce((s, c) => s + c.confidence, 0) / scored.length
    : 0;

  return {
    pillarId,
    score: clamp(score, PORTFOLIO_CONFIG.SCORE_MIN, PORTFOLIO_CONFIG.SCORE_MAX),
    confidence: clamp(confidence, 0, 1),
    weight,
    rationale: rationales.join(' | '),
    citations: [...citations].slice(0, 10),
    sourceReportIds,
    missing: false,
    // Part C SP2 — only attach when one or more BINDING constraints fired.
    ...(constraints.length > 0 ? { appliedConstraints: constraints } : {}),
    ...factExtras,
  };
}
